/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2025, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 */
import { Inject, Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BULB_BUSY_COVER_SERVICE } from '../../../app/angularjs.providers';
import { BulbCKEStateService } from './bulb-cke-state.service';
import { UppyDashboardService } from '../../../app/asset-uploads/uppy-dashboard.service';
import { AssetUploadHttpService } from '../../../app/asset-uploads/http.service';
import { AeroDialogService } from '../../../aero/aero-dialog/aero-dialog.service';
import { TranslationService } from '../../../common/translation.service';
import { SerialSaveService } from '../../../save/serial-save.service';
import { UnitStateStore } from '../../../unit/unit-state.store';
import { UppyAllowedFileTypes } from '../../../app/asset-uploads/types';
import { EditorDialogService } from './editor-dialog.service';
import { AssetUploadEventService } from '../../../app/asset-uploads/asset-upload-event.service';

/**
 * Service for image carousel.
 */
@Injectable({
  providedIn: 'root',
})
export class ImageCarouselService {

  /**
   * Block name for backend endpoint.
   */
  IMAGE_CAROUSEL = 'imagecarouselblock';

  /**
   * Constructs an instance of the class and injects required services.
   *
   * @param aeroDialogService - Service for handling dialog interactions.
   * @param assetUploadEventService - Service for managing asset upload events.
   * @param assetUploadHttpService - Service for making HTTP requests related to asset uploads.
   * @param bulbBusyCoverService - Service for handling busy cover state.
   * @param bulbCKEStateService - Service for managing CKEditor state.
   * @param editorDialogService - Service for handling editor-related dialogs.
   * @param http - Angular's HttpClient for making HTTP requests.
   * @param serialSaveService - Service for handling sequential save operations.
   * @param translationService - Service for managing translations.
   * @param uppyDashboardService - Service for handling Uppy file upload dashboard.
   * @param unitStateStore - Service for managing unit state.
   */
  constructor(
    private aeroDialogService: AeroDialogService,
    private assetUploadEventService: AssetUploadEventService,
    private assetUploadHttpService: AssetUploadHttpService,
    @Inject(BULB_BUSY_COVER_SERVICE) private bulbBusyCoverService: any,
    private bulbCKEStateService: BulbCKEStateService,
    private editorDialogService: EditorDialogService,
    private http: HttpClient,
    private serialSaveService: SerialSaveService,
    private translationService: TranslationService,
    private uppyDashboardService: UppyDashboardService,
    private unitStateStore: UnitStateStore,
  ) { }

  /**
   * Open uppy upload dialog for image selection.
   * @param event - The custom event containing content block data.
   */
  handleAddingSlideToCarousel = (event: CustomEvent) => {
    const uppyAllowedImageFileTypes: UppyAllowedFileTypes[] = this.getAllowedExtensionsForUppy('image');
    this.bulbCKEStateService.assetType.next('IMAGE');
    this.uppyDashboardService.open({
      embed: false,
      assetLibrary: true,
      multiSelect: true,
      allowedFileTypes: uppyAllowedImageFileTypes,
      uploadState: 'imageCarouselSlide',
      contentBlockData: event,
    });
  };

  /**
   * Insert Images in Image Carousel once all image assets are uploaded.
   * @param event - The custom event containing content block data, editor, blockId.
   */
  handleImageCarouselSlideAdd = (event: CustomEvent) => {
    const response = event.detail.data;
    const data = event.detail.data.contentBlockData;
    const { blockId } = data.detail;
    const { editor } = data.detail;
    const { uploadedAssets } = response.response;
    data.noOfAssets = uploadedAssets?.length ?? 1;
    if (response.status === 'PROCESSING_SUCCESS' && response.uploadState === 'imageCarouselSlide') {
      const hideBusy: () => void = this.bulbBusyCoverService.showBusy();
      const uploadSub = this.assetUploadHttpService
        .fetchAllAssetLibraryAsset(uploadedAssets ?? [response.response.entry.id]).subscribe(
          (assets) => {
            const allEntries = assets.map((asset) => asset.entry);
            if (allEntries.every((entry) => entry.status === 'COMPLETE')) {
              if (this.bulbCKEStateService.getDesktopUploadSelectionMode()) {
                this.bulbCKEStateService.setDesktopUploadSelectionMode(false);
              }
              this.insertImageInCarousel(allEntries, blockId, editor, data);
            } else if (allEntries.some((entry) => entry.status === 'ERROR')) {
              this.aeroDialogService.showDialog({
                icon: 'fa fa-exclamation-triangle',
                iconClass: 'aero-alert',
                title: this.translationService.translateInstant('modal_oops_title'),
                detail: this.translationService.translateInstant('modal_image_heic_warning'),
                primaryLabel: this.translationService.translateInstant('button_ok'),
                closeOnPrimary: true,
                showSecondary: false,
              });
            }
            hideBusy();
            uploadSub.unsubscribe();
          },
        );
    }
  };

  /**
   * Remove current image from Image Carousel.
   * @param event - The custom event containing blockId and image index.
   */
  handleRemovingSlideFromCarousel = (event: CustomEvent) => {
    const { blockId } = event.detail;

    const patch = [{
      updateAction: 'removeImage',
      selectedImageIndex: event.detail.imageIndex,
    }];

    // Remove image on the server side:
    this.serialSaveService.save({
      showBusy: true,
      preventRetry: false,
      save: () => this.updateImageCarousel(blockId, patch)
        .then((response) => {
          angular.copy(response, CKEBulbData.page.unit.contentBlocks[blockId]);

          // Update share status to reflect that the page has been edited:
          const ShareTransition = this.unitStateStore.getShareTransitionEnum();
          this.unitStateStore.transitionShareStatus(ShareTransition.EDIT);

          const customSlideAddedEvent = new CustomEvent('imageCarouselSlideRemoved', {
            detail: {
              contentBlock: response,
              currentIndex: event.detail.imageIndex,
            },
          });
          window.dispatchEvent(customSlideAddedEvent);
        }),
    });
  };

  /**
   * Update Image Carousel Caption.
   * @param event - The custom event containing blockId, image index and new caption.
   */
  updateCarouselCaption = (event: CustomEvent) => {
    const { blockId } = event.detail;

    const patch = [{
      carouselCaption: this.encodeStringToHtmlEntities(event.detail.newCaption),
      selectedImageIndex: event.detail.imageIndex,
    }];

    this.serialSaveService.save({
      showBusy: false,
      preventRetry: false,
      save: () => this.updateImageCarousel(blockId, patch)
        .then((response) => {

          // Update share status to reflect that the page has been edited:
          const ShareTransition = this.unitStateStore.getShareTransitionEnum();
          this.unitStateStore.transitionShareStatus(ShareTransition.EDIT);

          const carouselCaptionEvent = new CustomEvent('updateCarousel', {
            detail: {
              contentBlock: response,
            },
          });
          window.dispatchEvent(carouselCaptionEvent);
        }),
    });
  };

  /**
   * Handle Images crop process.
   * @param event - The custom event containing editor, blockId, crop image details, index.
   */
  handleImageCarouselCrop = (event: CustomEvent) => {
    const { blockId } = event.detail;
    const { croppedImages } = event.detail;
    const { selectedImageIndex } = event.detail;
    const { editor } = event.detail;

    this.serialSaveService.save({
      showBusy: true,
      preventRetry: false,
      save: () => this.updateImageCarousel(blockId, croppedImages)
        .then((modifiedContentBlock) => {
          // Update the controllers content-block content:
          angular.copy(modifiedContentBlock, CKEBulbData.page.unit.contentBlocks[blockId]);
          let bulbCompletedImageCrop: CustomEvent;
          if (selectedImageIndex === 0) {
            bulbCompletedImageCrop = new CustomEvent('imageCarouselUploadComplete', {
              detail: {
                contentBlock: modifiedContentBlock,
                currentEditor: event.detail.editor,
              },
            });
            // Dispatch the event to cke, so it can handle updating the model data:
            this.bulbCKEStateService.imageCarouselCropUploadCompleteEvent.next(bulbCompletedImageCrop);
          } else {
            bulbCompletedImageCrop = new CustomEvent('imageCarouselSlideAdded', {
              detail: {
                contentBlock: modifiedContentBlock,
                currentIndex: event.detail.selectedImageIndex - 1,
              },
            });
            window.dispatchEvent(bulbCompletedImageCrop);
          }

          // Update share status to reflect that the page has been edited:
          const ShareTransition = this.unitStateStore.getShareTransitionEnum();
          this.unitStateStore.transitionShareStatus(ShareTransition.EDIT);
          const { noOfAssetsUploaded } = event.detail;
          let orientation = null;
          if (blockId) {
            const figureElement = document.querySelector(`figure[bulb-block-id="${blockId}"]`);

            if (figureElement) {
              const possibleOrientations = ['FREEFORM', 'LANDSCAPE', 'PORTRAIT', 'SQUARE'];

              orientation = possibleOrientations.find((orientation) => figureElement.classList.contains(orientation));
            }
          }
          if (noOfAssetsUploaded === 1) {
            const openAssetLibraryEvent = new CustomEvent('addImageToCarousel', {
              detail: {
                event,
                eventFiredFromEditor: true,
                blockId,
                orientation,
                imageIndex: selectedImageIndex,
                editor,
              },
            });
            window.dispatchEvent(openAssetLibraryEvent);
          }
        }),
    });
  };

  /**
   * Open uppy upload dialog for uploading images and process image carousel insertion.
   * @param event - The custom event containing orientation.
   */
  handleImageCarouselButtonClick = (event: CustomEvent) => {
    event.stopPropagation();
    const uppyAllowedImageFileTypes: UppyAllowedFileTypes[] = this.getAllowedExtensionsForUppy('image');
    const { orientation } = event.detail;
    // Image Carousel is Added from Media options.
    this.bulbCKEStateService.setImageCarouselCreation(true);
    this.bulbCKEStateService.assetType.next('IMAGE');
    this.uppyDashboardService.open({
      embed: false,
      assetLibrary: true,
      multiSelect: true,
      allowedFileTypes: uppyAllowedImageFileTypes,
      uploadState: 'imageCarousel',
    });

    const assetEvents = this.assetUploadEventService.uppyEventEmitter.subscribe((event) => {
      const { uploadedAssets } = event.response;
      if (event.status === 'PROCESSING_SUCCESS' && event.uploadState === 'imageCarousel') {
        if (this.bulbCKEStateService.uploadToServer.value) {
          const hideBusy: () => void = this.bulbBusyCoverService.showBusy();
          this.bulbCKEStateService.uploadToServer.next(false);
          const uploadSub = this.assetUploadHttpService
            .fetchAllAssetLibraryAsset(uploadedAssets ?? [event.response.entry.id]).subscribe(
              (assets) => {
                const allEntries = assets.map((asset) => asset.entry);
                if (allEntries.every((entry) => entry.status === 'COMPLETE')) {
                  const addedImageEntryIds = allEntries.map((entry) => entry.id).toString();
                  const imageDetails = {
                    assetLibraryEntryIds: addedImageEntryIds,
                    orientation,
                  };
                  if (this.bulbCKEStateService.getDesktopUploadSelectionMode()) {
                    this.bulbCKEStateService.setDesktopUploadSelectionMode(false);
                    this.bulbCKEStateService.setImageCarouselCreation(false);
                    // Create the image-carousel block
                    this.createImageCarouselBlock(
                      imageDetails,
                      orientation,
                      uploadedAssets?.length ?? 1,
                    );
                  } else {
                    this.createImageCarouselBlock(
                      imageDetails,
                      orientation,
                      uploadedAssets?.length ?? 1,
                    );
                  }
                } else if (allEntries.some((entry) => entry.status === 'ERROR')) {
                  this.aeroDialogService.showDialog({
                    icon: 'fa fa-exclamation-triangle',
                    iconClass: 'aero-alert',
                    title: this.translationService.translateInstant('modal_oops_title'),
                    detail: this.translationService.translateInstant('modal_image_heic_warning'),
                    primaryLabel: this.translationService.translateInstant('button_ok'),
                    closeOnPrimary: true,
                    showSecondary: false,
                  });
                }
                hideBusy();
                uploadSub.unsubscribe();
              },
            );
        }
        assetEvents.unsubscribe();
      }
    });
  };

  /**
   * Create image carousel block.
   * @param imageDetails post object containing asset ids and orientation.
   * @param orientation orientation mode.
   * @param noOfAssets No of image assets uploaded.
   */
  createImageCarouselBlock = (
    imageDetails: { assetLibraryEntryIds: string; orientation: string; },
    orientation: string,
    noOfAssets: number,
  ) => {
    this.bulbCKEStateService.uploadToServer.next(true);
    this.serialSaveService.save({
      preventRetry: false,
      showBusy: true,
      save: () => this.insertImageCarousel(imageDetails)
        .then((response) => {
          CKEBulbData.page.unit.contentBlocks[response.contentBlock.id] = response.contentBlock;
          this.bulbCKEStateService.handleSaveOperation.next(true);
          const stopBusyIndicator = this.bulbCKEStateService.stopBusyIndicator.value;
          if (stopBusyIndicator) {
            stopBusyIndicator();
            this.bulbCKEStateService.stopBusyIndicator.next(null);
          }
          // Update share status to reflect that the page has been edited:
          const ShareTransition = this.unitStateStore.getShareTransitionEnum();
          this.unitStateStore.transitionShareStatus(ShareTransition.EDIT);
          const customImageCarouselUploadCompleteEvent = new CustomEvent('imageCarouselUploadComplete', {
            detail: {
              contentBlock: response.contentBlock, page: CKEBulbData.page, orientation, noOfAssets,
            },
          });

          this.bulbCKEStateService.imageCarouselUploadCompleteEvent.next(customImageCarouselUploadCompleteEvent);
        }),
    });
  };

  /**
   * Insert the uploaded image assets in the image carousel block.
   * @param addedImageEntry contains the uploaded image details.
   * @param blockId Image carousel block id.
   * @param editor Current CKEditor instance.
   * @param data Object containing details like image index and number of assets uploaded.
   * @private
   */
  private insertImageInCarousel(addedImageEntry: any[], blockId: string, editor, data) {
    const addedImageEntryIds = addedImageEntry.map((entry) => entry.id);
    // Add image on the server-side:
    const patch = [{
      assetLibraryEntryIds: addedImageEntryIds,
      updateAction: 'addImage',
      selectedImageIndex: data.detail.imageIndex,
    }];

    this.serialSaveService.save({
      showBusy: true,
      preventRetry: false,
      save: () => this.updateImageCarousel(blockId, patch).then((response) => {
        angular.copy(response, CKEBulbData.page.unit.contentBlocks[blockId]);
        this.bulbCKEStateService.handleSaveOperation.next(true);

        // Update share status to reflect that the page has been edited:
        const ShareTransition = this.unitStateStore.getShareTransitionEnum();
        this.unitStateStore.transitionShareStatus(ShareTransition.EDIT);
        const stopBusyIndicator = this.bulbCKEStateService.stopBusyIndicator.value;
        if (stopBusyIndicator) {
          stopBusyIndicator();
          this.bulbCKEStateService.stopBusyIndicator.next(null);
        }
        this.editorDialogService.showOrientationDialog(
          false,
          response.carouselImages[data.detail.imageIndex + 1].fullImageAsset.url,
          response,
          editor,
          response.orientation,
          data.detail.imageIndex + 1,
          data.noOfAssets,
        );
        const editingView = editor.editing.view;
        editingView.focus();
      }),
    });
  }

  /**
   * Trigger Backend API to Update image carousel.
   * @param blockId Image carousel block id.
   * @param patch Object containing required information such as block id, action, cropped details etc.
   * @private
   */
  private updateImageCarousel(blockId: string, patch: any): Promise<any> {
    return lastValueFrom(
      this.http.patch(
        `units/${CKEBulbData.page.unit.id}/${this.IMAGE_CAROUSEL}/${blockId}`,
        patch,
      ),
    );
  }

  /**
   * Trigger Backend API to insert image carousel.
   * @param imageDetails Image asset details.
   * @private
   */
  private insertImageCarousel(imageDetails: any): Promise<any> {
    const params = new HttpParams().set('cke', 'true');
    return lastValueFrom(
      this.http.post(
        `units/${CKEBulbData.page.unit.id}/${this.IMAGE_CAROUSEL}`,
        imageDetails,
        { params },
      ),
    );
  }

  /**
   * Fetches allowed file extensions for the specific asset.
   * @param asset asset name for example image.
   * @private
   */
  private getAllowedExtensionsForUppy(asset: string): UppyAllowedFileTypes[] {
    const extensionsFromConfig = (CKEBulbData.CONFIG)[`${asset}`].extensions.split(',');
    const extensionArray = [];
    for (let i = 0; i < extensionsFromConfig.length; i += 1) {
      extensionArray.push(`.${extensionsFromConfig[i]}`);
    }
    return extensionArray;
  }

  /**
   * Helper function to encode string to html entities.
   *
   * @param str string to encode.
   * @returns {String} Encoded string.
   */
  private encodeStringToHtmlEntities(str: string): string {
    return str.replace(/[\u00A0-\u9999<>\&]/g, (i) => `&#${i.charCodeAt(0)};`);
  }
}
